import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {Autocomplete, Button, Card, CardContent, Grid, MenuItem, Stack, TextField, Typography} from "@mui/material";
import * as Yup from "yup";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {useTranslation} from "react-i18next";
import {useRegistryFuelCardsRepository} from "../../../api/repositories/RegistryFuelCardsRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useRegistryFuelCardStatuses} from "../constants/useRegistryFuelCardStatuses";
import {useRegistryFuelCardUse} from "../constants/useRegistryFuelCardUse";
import {useRegistryFuelCardProviders} from "../constants/useRegistryFuelCardProviders";
import {useRegistryFuelCardBusiness} from "../constants/useRegistryFuelCardBusiness";
import {useHasScope} from "../../../../../config/globalConfig";
import {SCOPE_REGISTRY_FUEL_CARD_ADD} from "../constants/RegistryFuelCardAuth";
import NotAuthorizedDialog from "../../../../authentication/component/NotAuthorizedDialog";
import {DatePicker} from "@mui/x-date-pickers";
import {useNavigate} from "react-router-dom";
import {AMOUNT_REG_EXP} from "../constants/FuelsConstants";
import FormSelect from "../../../experiments/forms/FormSelect";

function RegistryFuelCard() {
    const {t} = useTranslation('finance', {keyPrefix: 'registryFuelCard'});
    const { t: tg } = useTranslation("translation");
    
    // Use Context
    const {
        globalData: { user },
    } = useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
    }));
    
    const { postRegistryCard} = useRegistryFuelCardsRepository();
    const {dispatchGlobalData, globalData } = useContext(GlobalContext);
    const [mobilityStores, setMobilityStores] = useState<any[]>([]);

    const {statuses, getRegistryFuelCardStatuses} = useRegistryFuelCardStatuses();
    const {cardUseList, getRegistryFuelCardUse} = useRegistryFuelCardUse();
    const {providers, getRegistryFuelCardProviders} = useRegistryFuelCardProviders();
    const {business, getRegistryFuelCardBusiness} = useRegistryFuelCardBusiness();
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const retrieveInitialValues = () => {
        return {
            id: "",
            cardNumber: "",
            provider: "",
            cardUse: "",
            idMsAssignee: "",
            msAssignee: null,
            refereeAssignee: "",
            business: "",
            dueDate: null,
            status: "",
            plafond: "",
            note: "",
            legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
            dealer: ""
        }
    }
    
    const validationSchema = Yup.object({
        cardNumber: Yup.number().typeError(t('messages.requiredCheckNumber')),
        provider: Yup.string().required(t('messages.requiredCheck')),
        cardUse: Yup.string().required(t('messages.requiredCheck')),
        idMsAssignee: Yup.string().required(t('messages.requiredCheck')),
        msAssignee: Yup.object().required(t('messages.requiredCheck')),
        refereeAssignee: Yup.string(),
        business: Yup.string().required(t('messages.requiredCheck')),
        dueDate: Yup.date().required(t('messages.requiredCheck')).nullable().typeError(t('messages.invalidDate')),
        status: Yup.string().required(t('messages.requiredCheck')),
        plafond: Yup.string().nullable().matches(AMOUNT_REG_EXP, {
            excludeEmptyString: true,
            message: t('messages.invalidPlafond')
        }),
        note: Yup.string(),
        dealer: Yup.string().required(t('messages.requiredCheck')),
        legalEntity: Yup.string().required(t('messages.requiredCheck')),
    });

    const {handleSubmit, control, reset, setValue} = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });

    const onSubmit = (payload: any) => {
        var request = {...payload};
        request.msAssignee = request.msAssignee.name;
        request.plafond *= 100;
        postRegistryCard(request).then((data) => {
            if (data && data.status === "OK") {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("messages.addedCardSuccess"),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                navigate("/finance/registryFuelCards")
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t(`messages.${data.message}`),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        })
    }
    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setMobilityStores([]);
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
            return {
                label: tg(`dealers.${x.name}`),
                value: x.name,
            };
        });
        setDealers(dealers);
        setMobilityStoreOptions(value);
    }

    function setMobilityStoreOptions(value: any) {
        const mobilityStores = globalData.user.mobilityStores.filter((m:any) => m.sapId !== "0" && m.tenant == value)
            .filter((value:any, index:any, self:any) =>
                index === self.findIndex((t:any) => (
                    t.name === value.name
                ))).sort((a:any,b:any) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));

        setValue('msAssignee', null);
        setMobilityStores(mobilityStores);
    }

    useEffect(() => {
        setMobilityStoreOptions(legalEntityOptions[0].value)
    }, [legalEntityOptions.length ===1])

    return (
        <Card variant="outlined" sx={{margin: '15px'}}>
            <CardContent>
                <Stack direction={"column"} spacing={2} margin={"16px"}>
                    { hasScope(SCOPE_REGISTRY_FUEL_CARD_ADD) ?
                        <form onSubmit={
                            handleSubmit(data => onSubmit(data), errors => console.log(errors))} noValidate>
                            <Grid container item xs={12}>
                                <Grid item xs={10}>
                                    <Typography variant={'body1'} fontWeight={'bold'}>
                                        {t('details.titleAdd')}
                                    </Typography>
                                </Grid>
                                <Grid container direction={'column'} rowGap={2}>
                                    <Grid container item>
                                        <Grid container item columnSpacing={3} rowSpacing={1}>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"cardNumber"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            fullWidth
                                                            required
                                                            id="cardNumber"
                                                            value={field.value}
                                                            label={t("details.cardNumber")}
                                                            type="number"
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}/>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"provider"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            style={{width: "100%"}}
                                                            select
                                                            required
                                                            label={t('details.provider')}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        >
                                                            {providers.map((provider) => (
                                                                <MenuItem key={provider.value} value={provider.value}>
                                                                    {getRegistryFuelCardProviders(provider.value)}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}/>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"business"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            style={{width: "100%"}}
                                                            select
                                                            required
                                                            label={t('details.business')}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        >
                                                            {business.map((bsn) => (
                                                                <MenuItem key={bsn.value} value={bsn.value}>
                                                                    {getRegistryFuelCardBusiness(bsn.value)}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"cardUse"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            style={{width: "100%"}}
                                                            select
                                                            required
                                                            label={t('details.cardUse')}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        >
                                                            {cardUseList.map((cardUse) => (
                                                                <MenuItem key={cardUse.value} value={cardUse.value}>
                                                                    {getRegistryFuelCardUse(cardUse.value)}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"msAssignee"}
                                                    render={({field, fieldState}) => (
                                                        <Autocomplete
                                                            id="dms-list"
                                                            options={mobilityStores}
                                                            getOptionLabel={(option: any) => option.name || ""}
                                                            value={field.value}
                                                            onChange={(event: any, newValue: any) => {
                                                                field.onChange(newValue);
                                                                setValue("idMsAssignee", newValue ? newValue.sapId : "");
                                                            }}
                                                            isOptionEqualToValue={(option:any, value:any) => {
                                                                if (option.hasOwnProperty("name")) {
                                                                    return option.name === value.name;
                                                                }
                                                                return option === value;
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} label={t("details.msAssignee")} variant="standard" />
                                                            )}
                                                        />
                                                    )}/>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"idMsAssignee"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            fullWidth
                                                            id="idMsAssignee"
                                                            required
                                                            disabled={true}
                                                            value={field.value}
                                                            label={t("details.idMsAssignee")}
                                                            type="text"
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}/>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"refereeAssignee"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            fullWidth
                                                            id="refereeAssignee"
                                                            value={field.value}
                                                            label={t("details.refereeAssignee")}
                                                            type="text"
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}/>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"dueDate"}
                                                    render={({field, fieldState}) => (
                                                        <DatePicker
                                                            label={t("details.dueDate")}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            renderInput={(params) =>
                                                                <TextField
                                                                    {...params}
                                                                    fullWidth
                                                                    required
                                                                    type="text"
                                                                    label={t("details.dueDate")}
                                                                    onChange={field.onChange}
                                                                    error={!!fieldState.error}
                                                                    helperText={fieldState.error?.message}

                                                                />}
                                                            inputFormat={"L"}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"status"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            style={{width: "100%"}}
                                                            select
                                                            required
                                                            label={t('details.status')}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        >
                                                            {statuses.map((status) => (
                                                                <MenuItem key={status.value} value={status.value}>
                                                                    {getRegistryFuelCardStatuses(status.value)}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Controller
                                                    control={control}
                                                    name={"plafond"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            fullWidth
                                                            id="plafond"
                                                            value={field.value}
                                                            label={t("details.plafond")}
                                                            type="text"
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <FormSelect
                                                    name={"legalEntity"}
                                                    control={control}
                                                    props={{
                                                        label: tg("shared.legalEntity"),
                                                        fullWidth: true,
                                                        required: true,
                                                        onChange: (e: any) => {
                                                            handleChangeLegalEntities(e.target.value)
                                                        },
                                                    }}
                                                    
                                                    items={legalEntityOptions.map((option: any) => (
                                                        <MenuItem key={option.value} 
                                                                    value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                />                       
                                            </Grid>
                                            <Grid item xs={3}>
                                            <FormSelect
                                                    name={"dealer"}
                                                    control={control}
                                                    props={{
                                                        label: tg("shared.dealer"),
                                                        fullWidth: true,
                                                        required: true,
                                                        disabled: dealers.length == 0
                                                    }}
                                                    items={dealers.map((option: any) => (
                                                        <MenuItem key={option.value}
                                                                    value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Controller
                                                    control={control}
                                                    name={"note"}
                                                    render={({field, fieldState}) => (
                                                        <TextField
                                                            fullWidth
                                                            id="note"
                                                            value={field.value}
                                                            label={t("details.note")}
                                                            type="text"
                                                            onChange={field.onChange}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid container item xs={12} rowGap={2} rowSpacing={1} columnSpacing={3}
                                      justifyContent={"flex-end"}>
                                    <Grid item>
                                        <Button variant={"outlined"} color={"accent"}
                                                onClick={() => {
                                                    reset(retrieveInitialValues());
                                                    navigate("/finance/registryFuelCards")
                                                }}>
                                            {t('details.buttons.cancel')}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button color={"accent"} type={"submit"}>
                                            {t('details.buttons.save')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                        : <NotAuthorizedDialog/>
                    }
                </Stack>
            </CardContent>
        </Card>
    )
}

export default RegistryFuelCard;